import { DocumentNode, useQuery } from '@apollo/client';
import { TFunction } from 'i18next';
import { partition } from 'lodash';
import { tokenAlternation } from '../../../core/utils/chipEditor.ts';
import { FieldDefinition } from '../fieldsConfigData.ts';
import {
    CREATE_HEADER_LLM_EXTRACTOR_CONFIG,
    CREATE_LINE_ITEM_LLM_EXTRACTOR_CONFIG,
    GET_HEADER_LLM_EXTRACTOR_CONFIG,
    GET_LINE_ITEM_LLM_EXTRACTOR_CONFIG,
} from '../../queries.ts';

export type RuleLevel = 'header' | 'lineItem';

interface RuleLevelConfig {
    titleKey: string;
    descriptionKey: string;
    // the level's name in the URL: ?level= of a new rule, and the prefix of its list's search and page
    param: string;
    query: DocumentNode;
    createMutation: DocumentNode;
    // what a new rule reads; a line item rule runs once per row, so it reads the row
    defaultInputs: string[];
}

export const RULE_LEVELS: Record<RuleLevel, RuleLevelConfig> = {
    header: {
        titleKey: 'config:configPage.fineTuning.headerTitle',
        descriptionKey: 'config:configPage.fineTuning.headerDescription',
        param: 'document',
        query: GET_HEADER_LLM_EXTRACTOR_CONFIG,
        createMutation: CREATE_HEADER_LLM_EXTRACTOR_CONFIG,
        defaultInputs: ['Document'],
    },
    lineItem: {
        titleKey: 'config:configPage.fineTuning.lineItemTitle',
        descriptionKey: 'config:configPage.fineTuning.lineItemDescription',
        param: 'line-item',
        query: GET_LINE_ITEM_LLM_EXTRACTOR_CONFIG,
        createMutation: CREATE_LINE_ITEM_LLM_EXTRACTOR_CONFIG,
        defaultInputs: ['LineItemHeader', 'LineItem'],
    },
};

export const NEW_RULE_ID = 'new';

interface RuleFile {
    id: string;
    name: string;
    size: number;
    tokenCount: number;
    url: string;
}

export interface Rule {
    id: string;
    name?: string | null;
    isDefault?: boolean;
    partitionIds?: string[];
    isEnabled?: boolean;
    testingOnly?: boolean;
    prompt?: string;
    documentInput?: string[];
    fields?: string[];
    contextFiles?: RuleFile[];
    useRag?: boolean;
    tools?: string[];
    model?: string;
    reasoningEffort?: string | null;
    lineItemOperationsMode?: boolean;
}

export interface LlmExtractorInput {
    name: string;
    fieldPath?: string | null;
}

export interface RuleFormValues {
    // null while the rule has no name of its own; '' while one is being typed
    name: string | null;
    isDefault: boolean;
    partitionIds: string[];
    isEnabled: boolean;
    testingOnly: boolean;
    prompt: string;
    inputFields: string[];
    outputFields: string[];
    // the saved files, and the dropped ones until they are uploaded
    files: (RuleFile | File)[];
    useRag: boolean;
    tools: string[];
    model: string;
    reasoningEffort: string;
    lineItemOperationsMode: boolean;
}

// the backend's default model of a config (documents.models.LLMExtractorConfig.model); availableLlmModels doesn't say
export const DEFAULT_LLM_MODEL = 'gpt-5.6-luna';

export const toRuleFormValues = (rule: Partial<Rule>): RuleFormValues => ({
    name: rule.name || null,
    isDefault: rule.isDefault ?? false,
    partitionIds: rule.partitionIds ?? [],
    isEnabled: rule.isEnabled ?? true,
    testingOnly: rule.testingOnly ?? false,
    prompt: rule.prompt ?? '',
    inputFields: rule.documentInput ?? [],
    outputFields: rule.fields ?? [],
    files: rule.contextFiles ?? [],
    useRag: rule.useRag ?? false,
    tools: rule.tools ?? [],
    model: rule.model || DEFAULT_LLM_MODEL,
    reasoningEffort: rule.reasoningEffort ?? '',
    lineItemOperationsMode: rule.lineItemOperationsMode ?? false,
});

type RuleScope = Pick<Rule, 'isDefault' | 'partitionIds'>;

export const getRuleScopeTitle = (rule: RuleScope, t: TFunction) => {
    if (rule.isDefault) return t('config:configPage.fineTuning.allClients');
    const partitionIds = rule.partitionIds ?? [];
    return partitionIds.length
        ? t('config:configPage.fineTuning.clients', { count: partitionIds.length, clients: partitionIds.join(', ') })
        : t('config:configPage.fineTuning.noClientsYet');
};

export const getRuleTitle = (rule: RuleScope & Pick<Rule, 'name'>, t: TFunction) =>
    rule.name || getRuleScopeTitle(rule, t);

// the rules of one level, the default rule first, as it runs first; the client rules in their saved order
export const useRules = (level: RuleLevel, channelId: string | undefined, gqlDocumentType: string, skip = false) => {
    const { data, loading, refetch } = useQuery<{ rules: Rule[] }>(RULE_LEVELS[level].query, {
        variables: { channelId, documentType: gqlDocumentType },
        // the assistance view asks once per header field group
        fetchPolicy: 'cache-and-network',
        skip: skip || !channelId || !gqlDocumentType,
    });
    const rules = (data?.rules ?? []).toSorted((a, b) => Number(!!b.isDefault) - Number(!!a.isDefault));
    return { rules, loading: loading && !data, refetch };
};

// a new rule of the level, or a copy of a saved rule: a client rule without client numbers, since a client number
// can be in one rule only; its Knowledge files stay with the original
export const getRuleDraft = (
    level: RuleLevel,
    rules: Rule[],
    copySource: Rule | undefined,
    t: TFunction,
    fields: string[] = []
) => {
    if (!copySource) {
        return {
            title: t('config:configPage.fineTuning.newRule'),
            values: toRuleFormValues({
                isDefault: !rules.some((rule) => rule.isDefault),
                documentInput: RULE_LEVELS[level].defaultInputs,
                fields,
            }),
        };
    }
    const values = toRuleFormValues(copySource);
    return {
        title: t('config:configPage.fineTuning.copyTitle', { title: getRuleTitle(values, t) }),
        values: {
            ...values,
            isDefault: false,
            partitionIds: [],
            files: [],
            name: values.name && t('config:configPage.fineTuning.copyName', { name: values.name }),
        },
    };
};

// a client rule needs a client number, and a client number can be in one rule only
export const validateRuleClients = (
    partitionIds: string[],
    otherRules: Rule[],
    t: TFunction,
    isEmptyAllowed = false
) => {
    if (!partitionIds.length) return isEmptyAllowed || t('config:configPage.fineTuning.runsFor.clientsRequired');
    const otherRule = otherRules.find((rule) => rule.partitionIds?.some((id) => partitionIds.includes(id)));
    if (!otherRule) return true;
    return t('config:configPage.fineTuning.runsFor.clientInOtherRule', {
        client: partitionIds.find((id) => otherRule.partitionIds?.includes(id)),
        rule: getRuleTitle(otherRule, t),
    });
};

const LOOKUP_TYPE_TOOLS: Record<string, string> = {
    ClientLookupType: 'LookupClient',
    ContactLookupType: 'LookupContact',
    ArticleLookupType: 'LookupArticle',
};

// the backend only adds lookup options to a field when the channel has that lookup in its master data
const getLookupTool = (field: FieldDefinition) => {
    const options = Object.fromEntries((field.options ?? []).map(({ name, value }) => [name, value]));
    if (!field.enabled || options.perform_lookups === false) return null;
    const lookupTypes = [options.lookup_type, options.lookup_type_default];
    if (lookupTypes.includes('AddressLookupType')) {
        return field.name.startsWith('invoice_address') ? 'LookupInvoiceAddress' : 'LookupDeliveryAddress';
    }
    return lookupTypes.map((lookupType) => LOOKUP_TYPE_TOOLS[String(lookupType)]).find(Boolean) ?? null;
};

export const getMissingLookups = (channelFields: FieldDefinition[], outputFields: string[], tools: string[]) => {
    const isActive = (tool: string) =>
        tools.includes(tool) || (tool === 'LookupArticle' && tools.includes('QueryArticleMasterdata'));
    const missing = new Map<string, string[]>();
    outputFields.forEach((name) => {
        const field = channelFields.find((channelField) => channelField.name === name);
        const tool = field && getLookupTool(field);
        if (tool && !isActive(tool)) missing.set(tool, [...(missing.get(tool) ?? []), name]);
    });
    return [...missing].map(([tool, fields]) => ({ tool, fields }));
};

export const getRuleStatus = (rule: Pick<Rule, 'isEnabled' | 'testingOnly'>, t: TFunction) =>
    [
        rule.isEnabled === false && t('config:configPage.fineTuning.status.inactive'),
        rule.testingOnly && t('config:configPage.fineTuning.status.testDocumentsOnly'),
    ]
        .filter(Boolean)
        .join(' · ');

// a name typed by hand only joins the rule when it can't be an ordinary word ("Document" can, "EmailBody" can't)
export const isDistinctName = (name: string) => name.includes('_') || /[A-Z].*[A-Z]/.test(name);

export const isWordName = (name: string) => !isDistinctName(name);

// the names standing on their own in the text, not as part of a longer word
export const getNamedIn = (text: string, names: string[]) => {
    if (!names.length) return new Set<string>();
    const pattern = new RegExp(`(?<![A-Za-z0-9_])(?:${tokenAlternation(names)})(?![A-Za-z0-9_])`, 'g');
    return new Set(text.match(pattern) ?? []);
};

export const CJK_PATTERN = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u;

// Japanese and Korean attach particles to a word or leave out the spaces, so their labels match anywhere
export const getLabelPattern = (labels: string[]) => {
    const [cjkLabels, otherLabels] = partition(labels, (label) => CJK_PATTERN.test(label));
    const alternatives = [
        otherLabels.length && `(?<![\\p{L}\\d])(?:${tokenAlternation(otherLabels)})\\p{L}{0,2}(?![\\p{L}\\d])`,
        cjkLabels.length && tokenAlternation(cjkLabels),
    ];
    return new RegExp(alternatives.filter(Boolean).join('|'), 'giu');
};

// the names the editor shows as chips, longest first: a name that can be an ordinary word ("Document", "total") on
// its own only, any other anywhere, since today's prompts also have "customer_contact__contact_idein:"
export const getNamesInText = (text: string, names: string[]) => {
    if (!names.length) return new Set<string>();
    return new Set(text.match(new RegExp(tokenAlternation(names, isWordName), 'g')) ?? []);
};
